/**
 * Client List Page - Sprint 3 + Sprint 10 Branding
 * Displays clients with filtering and navigation to client detail
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { clientsAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { RefreshCw, Plus, Search, UserPlus, Users } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  Prospect: 'bg-slate-100 text-slate-700',
  Admitted: 'bg-avida-blue-100 text-avida-blue-700',
  Active: 'bg-emerald-100 text-emerald-700',
  Discharged: 'bg-amber-100 text-amber-700',
  Archived: 'bg-red-100 text-red-700'
};

const PAYER_PROGRAMS = [
  { value: 'Family Care', label: 'Family Care' },
  { value: 'IRIS', label: 'IRIS' },
  { value: 'iCare', label: 'iCare' },
  { value: 'Straight Medicaid', label: 'Straight Medicaid' },
  { value: 'Private Pay', label: 'Private Pay' }
];

const ClientListPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, canCreateClients } = useAuth();
  
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [payerFilter, setPayerFilter] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  
  // Create dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [newClient, setNewClient] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    payer_program: ''
  });

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status_filter = statusFilter;
      if (payerFilter) params.payer_program = payerFilter;
      params.include_archived = includeArchived;
      
      const response = await clientsAPI.list(params);
      setClients(response.data.clients);
      setTotal(response.data.total);
    } catch (err) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, payerFilter, includeArchived]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleCreateClient = async () => {
    if (!newClient.first_name.trim() || !newClient.last_name.trim()) {
      toast.error('First name and last name are required');
      return;
    }
    
    setCreateLoading(true);
    try {
      const data = {
        first_name: newClient.first_name.trim(),
        last_name: newClient.last_name.trim(),
        phone: newClient.phone || null,
        email: newClient.email || null,
        payer_program: newClient.payer_program || null
      };
      
      const response = await clientsAPI.create(data);
      toast.success('Client created successfully');
      setShowCreateDialog(false);
      setNewClient({ first_name: '', last_name: '', phone: '', email: '', payer_program: '' });
      
      // Navigate to the new client's detail page
      navigate(`/clients/${response.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create client');
    } finally {
      setCreateLoading(false);
    }
  };

  if (authLoading) {
    return (
      <Layout title="Clients">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-slate-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Client Management">
      <div className="space-y-6" data-testid="client-list-page">
        {/* Filters and Actions */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading">Clients</CardTitle>
                <CardDescription>{total} total clients</CardDescription>
              </div>
              {canCreateClients && (
                <Button 
                  onClick={() => setShowCreateDialog(true)} 
                  className="bg-avida-blue hover:bg-avida-blue-800"
                  data-testid="create-client-btn"
                >
                  <UserPlus className="h-4 w-4 mr-2" />New Client
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[200px]">
                <Label>Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input 
                    placeholder="Search by name or Medicaid ID..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </div>
              <div className="w-40">
                <Label>Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="status-filter">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="Prospect">Prospect</SelectItem>
                    <SelectItem value="Admitted">Admitted</SelectItem>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Discharged">Discharged</SelectItem>
                    <SelectItem value="Archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-44">
                <Label>Payer Program</Label>
                <Select value={payerFilter} onValueChange={setPayerFilter}>
                  <SelectTrigger data-testid="payer-filter">
                    <SelectValue placeholder="All programs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Programs</SelectItem>
                    {PAYER_PROGRAMS.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={fetchClients} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Client List */}
        <Card data-testid="client-list">
          <CardContent className="pt-6">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : clients.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                <p>No clients found</p>
                {canCreateClients && (
                  <Button variant="outline" className="mt-4" onClick={() => setShowCreateDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />Create First Client
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Name</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Payer</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Medicaid ID</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Phone</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((client) => (
                      <tr 
                        key={client.id} 
                        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/clients/${client.id}`)}
                        data-testid={`client-row-${client.id}`}
                      >
                        <td className="py-3 px-4">
                          <div className="font-medium text-slate-900">
                            {client.last_name}, {client.first_name}
                          </div>
                          {client.email && (
                            <div className="text-sm text-slate-500">{client.email}</div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={STATUS_COLORS[client.status] || 'bg-slate-100'}>
                            {client.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {client.payer_program || '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600 font-mono">
                          {client.medicaid_id || '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {client.phone || '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-500">
                          {new Date(client.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

      {/* Create Client Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Client</DialogTitle>
            <DialogDescription>Enter basic information to create a new client (Prospect status)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input 
                  value={newClient.first_name}
                  onChange={(e) => setNewClient({...newClient, first_name: e.target.value})}
                  placeholder="John"
                  data-testid="new-client-first-name"
                />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input 
                  value={newClient.last_name}
                  onChange={(e) => setNewClient({...newClient, last_name: e.target.value})}
                  placeholder="Doe"
                  data-testid="new-client-last-name"
                />
              </div>
            </div>
            <div>
              <Label>Phone</Label>
              <Input 
                value={newClient.phone}
                onChange={(e) => setNewClient({...newClient, phone: e.target.value})}
                placeholder="(555) 123-4567"
                data-testid="new-client-phone"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input 
                type="email"
                value={newClient.email}
                onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                placeholder="john.doe@example.com"
                data-testid="new-client-email"
              />
            </div>
            <div>
              <Label>Payer Program</Label>
              <Select value={newClient.payer_program} onValueChange={(v) => setNewClient({...newClient, payer_program: v})}>
                <SelectTrigger data-testid="new-client-payer">
                  <SelectValue placeholder="Select payer program" />
                </SelectTrigger>
                <SelectContent>
                  {PAYER_PROGRAMS.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateClient} 
              disabled={createLoading} 
              className="bg-avida-blue hover:bg-avida-blue-800"
              data-testid="submit-create-client"
            >
              {createLoading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </Layout>
  );
};

export default ClientListPage;
